import React, { Fragment, useState } from 'react'
import {nanoid} from 'nanoid'
import styled from 'styled-components'
import childrenToString from '../childrenToString'
import PrismCode from '..'
import LanguageSwitch from './LanguageSwitch'
import { generateIds } from './generateIds'
import { CodeTabs, Pree, Wrapper } from './styles'

const NonPreChild = styled.div`
  * {
    margin-top: 12px !important;
  }
`

type CodeProps = {
  children: React.ReactNode
  language: string
  languages?: string[]
  hideCopy?: boolean
  playgroundLink?: string
}

const getCodeChildren = (children: React.ReactNode) => {
  return React.Children.toArray(children).filter(c => typeof c !== 'string')
}
const getLanguage = (child: any): string | null => {
  return (child && child.props && child.props.props && child.props.props.className) || null
}

const CodePanel = ({ languages, children, ...props }: CodeProps) => {
  const [name] = useState(nanoid)
  const [activeIndex, setActiveIndex] = useState(0)
  const state = { activeIndex, name }
  const blocks = renderCodeBlocks({ children: getCodeChildren(children), ...props, ...state })

  return (
    <Wrapper>
      <LanguageSwitch languages={languages} setActiveIndex={setActiveIndex} {...state} />
      <Tabs name={name} languages={languages || [props.language]}>
        {blocks}
      </Tabs>
    </Wrapper>
  )
}

export default CodePanel

const Tabs = ({ name, children, languages }: { name: string; children: React.ReactNode[]; languages: string[] }) => {
  const showTabs = false
  if (!showTabs || languages.length < 2) return <Fragment>{children}</Fragment>
  const { tabs } = generateIds({ name, index: 0 })
  return (
    <CodeTabs.Tabs id={tabs}>
      {children.map((block, index) => {
        const { panel, button } = generateIds({ name, index })
        return (
          <CodeTabs.Tab
            key={panel}
            id={panel}
            aria-labelledby={button}
            data-option=""
            data-selected={index ? null : ''}
            role="tabpanel"
          >
            {block}
          </CodeTabs.Tab>
        )
      })}
    </CodeTabs.Tabs>
  )
}

type CodeBlocksProps = {
  children: React.ReactNode[]
  activeIndex: number
  hideCopy?: boolean
  playgroundLink?: string
  language?: string
}

const renderCodeBlocks = ({ children, activeIndex, hideCopy, playgroundLink, language }: CodeBlocksProps) => {
  const blocks = [children[activeIndex]]
  return blocks.map((child, index) => {
    const elemType = child && (child as any).props && (child as any).props.name
    if (elemType === 'pre') {
      const code = child && (child as any).props && ((child as any).props as any).children
      return (
        <Pree copy={!hideCopy} playgroundLink={playgroundLink} key={`block-${index}`}>
          <PrismCode className={`${getLanguage(code) || (language ? `language-${language}` : '')} line-numbers`}>
            {childrenToString(child)}
          </PrismCode>
        </Pree>
      )
    } else {
      return child
    }
  })
}
